"use client";

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";
import { Label } from "./label";

/**
 * RadioGroup provides a set of radio buttons where only one can be selected.
 * Built on Radix UI RadioGroup for full accessibility support.
 */
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
    /** Label for the radio group */
    legend?: string;
    /** Description for the group */
    description?: string;
    /** Error message */
    error?: string;
    /** Whether to visually hide the legend */
    hideLegend?: boolean;
  }
>(({ className, legend, description, error, hideLegend = false, children, ...props }, ref) => {
  const content = (
    <RadioGroupPrimitive.Root
      className={cn(
        styleDrivers.radioGroup.root,
        props.orientation === "horizontal" && "flex flex-wrap gap-6",
        className
      )}
      {...props}
      ref={ref}
    >
      {children}
    </RadioGroupPrimitive.Root>
  );

  if (!legend) {
    return content;
  }

  return (
    <fieldset className="space-y-3" aria-invalid={!!error}>
      <legend
        className={cn(
          "text-sm font-medium text-foreground",
          hideLegend && "sr-only"
        )}
      >
        {legend}
      </legend>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {content}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </fieldset>
  );
});

RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

export interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  /** Label text for the radio item */
  label?: string;
  /** Description text shown below the label */
  description?: string;
}

/**
 * RadioGroupItem is a single radio button within a RadioGroup.
 */
const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, label, description, id, ...props }, ref) => {
  const generatedId = React.useId();
  const radioId = id || generatedId;
  const descriptionId = description ? `${radioId}-description` : undefined;

  const radioElement = (
    <RadioGroupPrimitive.Item
      ref={ref}
      id={radioId}
      className={cn(styleDrivers.radioGroup.item, className)}
      aria-describedby={descriptionId}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className={styleDrivers.radioGroup.indicator}>
        <Circle className="h-2.5 w-2.5 fill-primary text-primary" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );

  if (!label && !description) {
    return radioElement;
  }

  return (
    <div className="flex items-start gap-3">
      {radioElement}
      <div className="grid gap-1 leading-none">
        {label && (
          <Label
            htmlFor={radioId}
            className={styleDrivers.radioGroup.label}
          >
            {label}
          </Label>
        )}
        {description && (
          <p id={descriptionId} className={styleDrivers.radioGroup.description}>
            {description}
          </p>
        )}
      </div>
    </div>
  );
});

RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

/**
 * RadioCard is a card-style radio option for more prominent choices.
 */
export interface RadioCardProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  /** Title for the card */
  title: string;
  /** Description for the card */
  description?: string;
  /** Icon to display */
  icon?: React.ReactNode;
}

const RadioCard = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioCardProps
>(({ className, title, description, icon, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(styleDrivers.radioGroup.card, className)}
      {...props}
    >
      {icon && (
        <div className="shrink-0 text-muted-foreground data-[state=checked]:text-primary">
          {icon}
        </div>
      )}
      <div className="flex-1 space-y-1">
        <div className="text-sm font-medium">{title}</div>
        {description && (
          <div className={styleDrivers.radioGroup.description}>{description}</div>
        )}
      </div>
      <div className="shrink-0">
        <div className="h-4 w-4 rounded-full border border-primary data-[state=checked]:bg-primary">
          <RadioGroupPrimitive.Indicator className={styleDrivers.radioGroup.indicator}>
            <Circle className="h-2 w-2 fill-primary-foreground text-primary-foreground" />
          </RadioGroupPrimitive.Indicator>
        </div>
      </div>
    </RadioGroupPrimitive.Item>
  );
});

RadioCard.displayName = "RadioCard";

export { RadioGroup, RadioGroupItem, RadioCard };
export default RadioGroup;
